export type RealtimeBackendMode = "stdb";

const REALTIME_MODE_STORAGE_KEY = "realtime.backend";
const REALTIME_MODE_QUERY_PARAM = "rtb";
const STDB_URI_STORAGE_KEY = "stdb.uri";
const STDB_URI_QUERY_PARAM = "stdbUri";
const STDB_DATABASE_STORAGE_KEY = "stdb.database";
const STDB_DATABASE_QUERY_PARAM = "stdbDb";

const DEFAULT_STDB_URI =
  process.env.BUN_PUBLIC_STDB_URI || "ws://127.0.0.1:3000";
const DEFAULT_STDB_DATABASE = process.env.BUN_PUBLIC_STDB_DB || "game";

type ResolverDependencies = {
  search?: string;
  getStorageItem?: (key: string) => string | null;
};

export interface StdbRuntimeConfig {
  uri: string;
  database: string;
}

function defaultGetStorageItem(key: string): string | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  return localStorage.getItem(key);
}

function normalizeMode(
  value: string | null | undefined,
): RealtimeBackendMode | null {
  const mode = value?.trim().toLowerCase();
  if (mode === "stdb") {
    return mode;
  }
  return null;
}

export function resolveRealtimeBackendMode(
  dependencies: ResolverDependencies = {},
): RealtimeBackendMode {
  const search =
    dependencies.search ??
    (typeof window === "undefined" ? "" : window.location.search);
  const getStorageItem = dependencies.getStorageItem ?? defaultGetStorageItem;
  const params = new URLSearchParams(search);

  const modeFromQuery = normalizeMode(params.get(REALTIME_MODE_QUERY_PARAM));
  if (modeFromQuery) {
    return modeFromQuery;
  }

  const modeFromStorage = normalizeMode(
    getStorageItem(REALTIME_MODE_STORAGE_KEY),
  );
  if (modeFromStorage) {
    return modeFromStorage;
  }

  return "stdb";
}

export function resolveStdbRuntimeConfig(
  dependencies: ResolverDependencies = {},
): StdbRuntimeConfig {
  const search =
    dependencies.search ??
    (typeof window === "undefined" ? "" : window.location.search);
  const getStorageItem = dependencies.getStorageItem ?? defaultGetStorageItem;
  const params = new URLSearchParams(search);

  const queryUri = params.get(STDB_URI_QUERY_PARAM)?.trim();
  const storageUri = getStorageItem(STDB_URI_STORAGE_KEY)?.trim() ?? "";
  const uri = queryUri || storageUri || DEFAULT_STDB_URI;

  const queryDatabase = params.get(STDB_DATABASE_QUERY_PARAM)?.trim();
  const storageDatabase =
    getStorageItem(STDB_DATABASE_STORAGE_KEY)?.trim() ?? "";
  const database = queryDatabase || storageDatabase || DEFAULT_STDB_DATABASE;

  return { uri, database };
}
